/**
 * ChatBackendService - Service unifié pour les appels API du chat contextuel
 * 
 * Ce service centralise tous les appels au backend pour le système de chat intelligent.
 * Il utilise le système d'authentification Firebase existant (getFirebaseToken).
 * 
 * Architecture:
 * - Réutilise le pattern de OpenAIService.js
 * - Appelle le backend Express.js via /api/chat/*
 * - Gère l'authentification avec Firebase tokens
 * - Gère les erreurs de manière cohérente
 */

import { getFirebaseToken } from '../firebase';

const BackEndUrl = process.env.REACT_APP_BACKEND_URL;

/**
 * Fonction utilitaire pour nettoyer les données sensibles dans les logs
 */
function sanitizeForLog(data) {
  if (!data || typeof data !== 'object') return data;
  
  const sanitized = { ...data };
  
  // Supprimer les données sensibles
  if (sanitized.context) {
    sanitized.context = {
      ...sanitized.context,
      // Garder seulement les informations non sensibles
      objective: sanitized.context.objective ? { titre: sanitized.context.objective.titre } : null,
      currentStep: sanitized.context.currentStep ? { titre: sanitized.context.currentStep.titre } : null,
      // Supprimer les données utilisateur sensibles
      userId: '[REDACTED]',
      competenceId: '[REDACTED]',
      // Garder les sources (important pour le diagnostic)
      selectedSources: sanitized.context.selectedSources ? 
        sanitized.context.selectedSources.map(source => ({
          url: source.url || source,
          title: source.title || '[No Title]',
          type: source.type || 'unknown'
        })) : null
    };
  }
  
  return sanitized;
}

/**
 * Fonction utilitaire pour faire des requêtes authentifiées au backend
 */
async function makeAuthenticatedRequest(endpoint, body) {
  if (!BackEndUrl) {
    throw new Error('REACT_APP_BACKEND_URL non configuré');
  }

  try {
    const token = await getFirebaseToken();
    if (!token) {
      throw new Error('Token Firebase manquant. Authentification requise.');
    }

    // 🔍 LOG DE DIAGNOSTIC : Afficher l'appel au backend
    console.log(`🔍 [Backend] Appel ${endpoint}:`, {
      url: `${BackEndUrl}/api/chat/${endpoint}`,
      body: sanitizeForLog(body), // Nettoyer les données sensibles
      timestamp: new Date().toISOString()
    });

    const response = await fetch(`${BackEndUrl}/api/chat/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`❌ [Backend] Erreur ${endpoint}:`, {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        timestamp: new Date().toISOString()
      });
      throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
    }

    const result = await response.json();
    
    // 🔍 LOG DE DIAGNOSTIC : Afficher la réponse
    console.log(`✅ [Backend] Réponse ${endpoint}:`, {
      success: true,
      processingTime: result.processingTime,
      answerLength: result.answer?.length || 0,
      citationsCount: result.citations?.length || 0,
      documentsCount: result.documents?.length || 0,
      timestamp: new Date().toISOString()
    });

    return result;
  } catch (error) {
    console.error(`❌ [ChatBackendService] Erreur ${endpoint}:`, {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
    throw error;
  }
}

/**
 * Fonction utilitaire pour faire des requêtes authentifiées DIRECTES (sans /api/chat/)
 * Utilisée pour la recherche KB qui doit utiliser le même endpoint que KnowledgeBase.js
 */
async function makeDirectAuthenticatedRequest(endpoint, body) {
  if (!BackEndUrl) {
    throw new Error('REACT_APP_BACKEND_URL non configuré');
  }

  try {
    const token = await getFirebaseToken();
    if (!token) {
      throw new Error('Token Firebase manquant. Authentification requise.');
    }

    // 🔍 LOG DE DIAGNOSTIC : Afficher l'appel DIRECT au backend
    console.log(`🔍 [Backend] Appel DIRECT ${endpoint}:`, {
      url: `${BackEndUrl}/${endpoint}`,
      body: sanitizeForLog(body),
      timestamp: new Date().toISOString()
    });

    const response = await fetch(`${BackEndUrl}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`❌ [Backend] Erreur DIRECT ${endpoint}:`, {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        timestamp: new Date().toISOString()
      });
      throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
    }

    const result = await response.json();
    
    console.log(`✅ [Backend] Réponse DIRECT ${endpoint} réussie:`, {
      success: true,
      documentsCount: result.documents?.length || 0,
      hasSynthesis: !!result.synthesis,
      timestamp: new Date().toISOString()
    });

    return result;
  } catch (error) {
    console.error(`❌ [ChatBackendService] Erreur DIRECT ${endpoint}:`, {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
    throw error;
  }
}

/**
 * Analyse l'intention de l'utilisateur
 * @param {string} message - Message de l'utilisateur
 * @param {Object} context - Contexte (objectif, stratégie, plan, étape, sources, notes)
 * @returns {Promise<Object>} - Résultat de l'analyse d'intention
 */
export async function analyzeIntent(message, context) {
  try {
    console.log(`🎯 [Backend] Analyse intention: "${message.substring(0, 100)}..."`);
    
    const result = await makeAuthenticatedRequest('analyze-intent', {
      message,
      context
    });

    console.log(`✅ [Backend] Intention détectée:`, {
      type: result.intent?.type,
      confidence: result.intent?.confidence,
      isRelevant: result.intent?.isRelevant
    });

    return result.intent;
  } catch (error) {
    console.error('[ChatBackendService] Erreur analyse intention:', error);
    throw new Error('Impossible d\'analyser l\'intention: ' + error.message);
  }
}

/**
 * Conversation avec Gemini
 * @param {string} message - Message de l'utilisateur
 * @param {Object} context - Contexte de la conversation
 * @param {Array} history - Historique des messages
 * @param {Object} options - Options (temperature, maxTokens, etc.)
 * @returns {Promise<Object>} - Réponse de Gemini
 */
export async function geminiConversation(message, context, history = [], options = {}) {
  try {
    console.log(`💬 [Backend] Conversation Gemini: "${message.substring(0, 100)}..."`);
    
    const result = await makeAuthenticatedRequest('conversation', {
      message,
      context,
      history,
      options
    });

    return {
      answer: result.answer,
      usage: result.usage,
      processingTime: result.processingTime
    };
  } catch (error) {
    console.error('[ChatBackendService] Erreur conversation Gemini:', error);
    throw new Error('Erreur lors de la conversation: ' + error.message);
  }
}

/**
 * Recherche web avec Gemini + Google Search
 * @param {string} query - Requête de recherche
 * @param {Object} context - Contexte de la recherche
 * @returns {Promise<Object>} - Résultats de la recherche
 */
export async function geminiWebSearch(query, context) {
  try {
    console.log(`🌐 [Backend] Recherche Gemini: "${query.substring(0, 100)}..."`);
    
    const result = await makeAuthenticatedRequest('search-web', {
      query,
      context
    });

    console.log(`✅ [Backend] Recherche Gemini réussie:`, {
      searchUsed: result.searchUsed,
      quota: result.quota,
      answerLength: result.answer?.length || 0
    });

    return {
      answer: result.answer,
      searchUsed: result.searchUsed,
      quota: result.quota,
      usage: result.usage,
      processingTime: result.processingTime
    };
  } catch (error) {
    console.error('[ChatBackendService] Erreur recherche Gemini:', error);
    throw new Error('Erreur lors de la recherche web: ' + error.message);
  }
}

/**
 * Recherche web avec Perplexity Sonar
 * @param {string} query - Requête de recherche
 * @param {Object} context - Contexte de la recherche
 * @returns {Promise<Object>} - Résultats avec citations
 */
export async function perplexityWebSearch(query, context) {
  try {
    console.log(`🔎 [Backend] Recherche Perplexity: "${query.substring(0, 100)}..."`);
    
    const result = await makeAuthenticatedRequest('search-perplexity', {
      query,
      context
    });

    console.log(`✅ [Backend] Recherche Perplexity réussie:`, {
      citationsCount: result.citations?.length || 0,
      relatedQuestionsCount: result.relatedQuestions?.length || 0,
      answerLength: result.answer?.length || 0
    });

    return {
      answer: result.answer,
      citations: result.citations || [],
      relatedQuestions: result.relatedQuestions || [], // ✨ NOUVEAU : Questions connexes
      usage: result.usage,
      processingTime: result.processingTime
    };
  } catch (error) {
    console.error('[ChatBackendService] Erreur recherche Perplexity:', error);
    throw new Error('Erreur lors de la recherche Perplexity: ' + error.message);
  }
}

/**
 * Analyse de sources web avec Perplexity
 * @param {Array} sources - Liste des sources à analyser
 * @param {string} question - Question à poser sur les sources
 * @param {Object} context - Contexte de l'analyse
 * @returns {Promise<Object>} - Analyse des sources
 */
export async function perplexityAnalyzeSources(sources, question, context) {
  try {
    console.log(`📊 [Backend] Analyse sources Perplexity:`, {
      question: question.substring(0, 100),
      sourcesCount: sources?.length || 0,
      sources: sources?.map(s => ({
        url: s.url || s,
        title: s.title || '[No Title]',
        type: s.type || 'unknown'
      }))
    });
    
    const result = await makeAuthenticatedRequest('analyze-sources', {
      sources,
      question,
      context
    });

    console.log(`✅ [Backend] Analyse sources réussie:`, {
      citationsCount: result.citations?.length || 0,
      answerLength: result.answer?.length || 0,
      processingTime: result.processingTime
    });

    return {
      answer: result.answer,
      citations: result.citations || [],
      usage: result.usage,
      processingTime: result.processingTime
    };
  } catch (error) {
    console.error('[ChatBackendService] Erreur analyse sources:', {
      error: error.message,
      sourcesCount: sources?.length || 0,
      question: question
    });
    throw new Error('Erreur lors de l\'analyse des sources: ' + error.message);
  }
}

/**
 * Recherche dans la base de connaissances
 * @param {string} query - Requête de recherche
 * @param {Object} context - Contexte (userId, objectiveId, etc.)
 * @returns {Promise<Object>} - Résultats de la recherche KB
 */
export async function searchKnowledgeBase(query, context) {
  try {
    console.log(`📚 [Backend] Recherche base connaissances: "${query.substring(0, 100)}..."`);
    
    // Extraire les informations essentielles du contexte
    const userId = context?.userId;
    const objectiveId = context?.competenceId || context?.objective?.id;
    
    if (!userId) {
      throw new Error('userId manquant dans le contexte');
    }
    
    if (!objectiveId) {
      throw new Error('objectiveId/competenceId manquant dans le contexte');
    }
    
    // Utiliser EXACTEMENT les mêmes paramètres que KnowledgeBase.js
    const requestData = {
      query: query.trim(),
      userId,
      objectiveId,
      options: {
        page: 0,
        limit: 10,
        threshold: 0.4,
        includeSynthesis: true,
        filters: {
          competenceId: objectiveId,
          userId
        }
      }
    };
    
    console.log(`🔍 [Backend] Paramètres KB identiques à KnowledgeBase.js:`, {
      query: requestData.query,
      userId: requestData.userId,
      objectiveId: requestData.objectiveId,
      options: requestData.options
    });

    // Utiliser le même endpoint que KnowledgeBase.js
    const result = await makeDirectAuthenticatedRequest('api/searchDocuments', requestData);

    console.log(`✅ [Backend] Recherche KB réussie:`, {
      documentsCount: result.documents?.length || 0,
      hasSynthesis: !!result.synthesis,
      success: result.success
    });

    // Retourner le résultat dans le même format que l'ancienne fonction
    return {
      answer: result.synthesis?.summary || '',
      documents: result.documents || [],
      synthesis: result.synthesis,
      usage: result.usage,
      processingTime: result.processingTime,
      success: result.success
    };
  } catch (error) {
    console.error('[ChatBackendService] Erreur recherche KB:', error);
    throw new Error('Erreur lors de la recherche dans la base de connaissances: ' + error.message);
  }
}

/**
 * Sauvegarde un message dans l'historique
 * @param {string} userId - ID de l'utilisateur
 * @param {string} conversationId - ID de la conversation
 * @param {Object} message - Message à sauvegarder
 * @returns {Promise<Object>} - Confirmation de sauvegarde
 */
export async function saveMessage(userId, conversationId, message) {
  try {
    const result = await makeAuthenticatedRequest('save-message', {
      userId,
      conversationId,
      message
    });

    return result;
  } catch (error) {
    console.error('[ChatBackendService] Erreur sauvegarde message:', error);
    // Ne pas bloquer l'application si la sauvegarde échoue
    console.warn('Message non sauvegardé, mais conversation continue');
    return { success: false, error: error.message };
  }
}

/**
 * Récupère l'historique d'une conversation
 * @param {string} userId - ID de l'utilisateur
 * @param {string} conversationId - ID de la conversation
 * @param {number} limit - Nombre de messages à récupérer
 * @returns {Promise<Array>} - Historique des messages
 */
export async function getConversationHistory(userId, conversationId, limit = 50) {
  try {
    const result = await makeAuthenticatedRequest('get-history', {
      userId,
      conversationId,
      limit
    });

    return result.messages || [];
  } catch (error) {
    console.error('[ChatBackendService] Erreur récupération historique:', error);
    // Retourner un tableau vide si l'historique n'est pas disponible
    return [];
  }
}

/**
 * Vérifie la disponibilité du backend
 * @returns {Promise<boolean>} - true si le backend est disponible
 */
export async function checkBackendHealth() {
  try {
    if (!BackEndUrl) {
      return false;
    }

    const response = await fetch(`${BackEndUrl}/api/chat/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.ok;
  } catch (error) {
    console.error('[ChatBackendService] Backend non disponible:', error);
    return false;
  }
}

// Export par défaut d'un objet avec toutes les fonctions
export default {
  analyzeIntent,
  geminiConversation,
  geminiWebSearch,
  perplexityWebSearch,
  perplexityAnalyzeSources,
  searchKnowledgeBase,
  saveMessage,
  getConversationHistory,
  checkBackendHealth
};
